/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * GOLD STANDARD solution page config. Other trades should follow this pattern.
 * Philosophy: full business platform for this trade, not just "estimate faster."
 */

import {
  Calculator,
  CheckCircle,
  Clock,
  DollarSign,
  FileText,
  Leaf,
  Layers,
  Ruler,
  TreePine,
  Truck,
  Wrench,
  X,
} from "lucide-react";

import type { SolutionTemplateConfig } from "@/components/landing/solution-template";
import {
  CTA,
  AI_TOOL_COUNT,
  STARTING_PRICE,
} from "@/content/landing/constants";

export const landscapingConfig: SolutionTemplateConfig = {
  tradeName: "Landscaping",
  tradeSlug: "landscaping",

  hero: {
    badge: "Landscaping Business Platform",
    title: "Software for landscaping contractors",
    titleHighlight: "from bid to maintenance visit.",
    description:
      "What did that patio job actually cost? Are your maintenance contracts profitable? Which crews are on budget? One platform for your costs, estimates, projects, services, and invoices — so you always know where you stand.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded landscaping costs",
      "Cancel anytime",
    ],
  },

  trustBar: {
    headline: "Built for landscaping contractors",
    proofPoints: [
      {
        icon: TreePine,
        text: "Installs, hardscape, irrigation, and maintenance",
      },
      { icon: DollarSign, text: "Nursery and supplier pricing management" },
      { icon: Wrench, text: "Service contracts with visit tracking" },
    ],
  },

  painPoints: {
    title: "Does this sound familiar?",
    description:
      "The daily reality of running a landscaping business without a system.",
    items: [
      {
        icon: Clock,
        title: "Prices that change with the season",
        description:
          "Sod is one price in April, another in July. You bid in spring, install in summer — and your margin disappeared. Your cost database should update when your suppliers do.",
      },
      {
        icon: DollarSign,
        title: "Three suppliers, nothing matches",
        description:
          "Shrubs from one nursery, pavers from another, mulch from a third. Three price lists, three formats, none of them in your estimating spreadsheet. You're pricing from memory.",
      },
      {
        icon: FileText,
        title: "Install jobs and maintenance contracts in different worlds",
        description:
          "You're quoting a patio install and a weekly mowing contract from the same truck — but they're in different spreadsheets, different invoicing, different everything. One system should handle both.",
      },
    ],
  },

  stats: [
    { value: "Installs + Maintenance", label: "Both in one system" },
    { value: `${AI_TOOL_COUNT} tools`, label: "AI assistant for your data" },
    { value: `$${STARTING_PRICE}/mo`, label: "Starting price" },
  ],

  howItWorks: {
    title: "The full lifecycle — one platform",
    description:
      "From nursery pricing to final invoice. Here's how a landscaper actually uses Contractor Co-Pilot.",
    steps: [
      {
        step: 1,
        title: "Set up your business",
        description:
          "Import your nursery pricing, configure hardscape materials, set labor rates for install and maintenance crews. Build work packages — a patio bundle (pavers + base + sand + edging + labor) you reuse on every job.",
        icon: TreePine,
      },
      {
        step: 2,
        title: "Win the work",
        description:
          "Upload site plans, measure hardscape areas and bed spaces, and pull in your work packages. Organize by zone — front, back, side. AI matches plants and materials from your catalog. Send a branded proposal before you leave the property.",
        icon: Calculator,
      },
      {
        step: 3,
        title: "Run the job",
        description:
          "Your accepted proposal becomes a project. Track crew hours, log material costs against the estimate, manage service visits for maintenance contracts. Budget vs. actuals — know if you're making money before the job is done.",
        icon: Wrench,
      },
      {
        step: 4,
        title: "Know your numbers",
        description:
          "Actual costs feed back into your cost database. Next season, your estimates are based on what you actually spent — not what you guessed. Ask Hank: 'What's my average margin on hardscape installs this year?'",
        icon: DollarSign,
      },
    ],
  },

  features: {
    title: "What you actually get",
    description:
      "Not generic construction software. A platform that understands installs, hardscape, irrigation, and recurring maintenance.",
    items: [
      {
        icon: Layers,
        title: "Work packages for common installs",
        description:
          "Build reusable bundles: patio install (pavers + base + sand + edging + labor), planting package (shrubs + mulch + soil + planting labor), irrigation zone (heads + pipe + valves + trenching). Use them on every estimate.",
        bullets: [
          "Material + labor bundles with production rates",
          "Configurable per job (adjust quantities, swap materials)",
          "Track how often each package is used",
          "Seasonal variants (spring planting vs fall cleanup)",
        ],
      },
      {
        icon: Truck,
        title: "Supplier and nursery pricing",
        description:
          "Import pricing from your nurseries and material suppliers. Track costs per vendor. When sod prices change in June, update once — every estimate uses the new number.",
        bullets: [
          "Import from Excel or CSV",
          "Per-supplier pricing with contact info",
          "Equipment TCO tracking (mowers, skid steers, trailers)",
          "Subcontractor management for specialized work",
        ],
      },
      {
        icon: Leaf,
        title: "Service contracts and visit tracking",
        description:
          "Maintenance contracts aren't projects — they're recurring services. CC tracks scheduled visits, logs costs per visit, and keeps service work separate from install work while keeping it all in one system.",
        bullets: [
          "Recurring and one-time service management",
          "Visit logging with duration and notes",
          "Cost tracking per service",
          "Service schedule with calendar view",
        ],
      },
      {
        icon: Ruler,
        title: "Blueprint takeoffs for hardscape",
        description:
          "Upload site plans and measure hardscape areas, bed spaces, and linear features directly on screen. Quantities feed into your estimate through work packages.",
        bullets: [
          "Measure areas, lengths, and counts on blueprints",
          "Scale calibration for accurate quantities",
          "Measurements link to estimate line items",
          "Multi-page site plan support",
        ],
      },
    ],
  },

  beforeAfter: {
    title: "The old way vs. the CC way",
    before: [
      { icon: X, text: "Pricing from memory or last season's numbers" },
      {
        icon: X,
        text: "Install estimates on a spreadsheet, maintenance on a notepad",
      },
      { icon: X, text: "Three supplier price lists that don't match" },
      { icon: X, text: "No idea if you made money until the season's over" },
      { icon: X, text: "Proposals via text message or handwritten quote" },
      { icon: X, text: "Crew hours tracked on paper timesheets" },
    ],
    after: [
      {
        icon: CheckCircle,
        text: "Current nursery and supplier pricing in one cost database",
      },
      {
        icon: CheckCircle,
        text: "Installs and maintenance contracts in the same system",
      },
      {
        icon: CheckCircle,
        text: "Branded proposals sent from the truck between jobs",
      },
      {
        icon: CheckCircle,
        text: "Budget vs. actuals on every job — know before it's too late",
      },
      {
        icon: CheckCircle,
        text: "Service visits tracked with costs per visit",
      },
      {
        icon: CheckCircle,
        text: "Time entries with approval workflow for crews",
      },
    ],
  },

  roi: {
    title: "What changes when your business is connected",
    description:
      "When your estimates feed into projects, your actuals feed back into your costs, and your maintenance contracts are tracked alongside your installs.",
    metrics: [
      { value: "Accurate", label: "Estimates based on real costs, not memory" },
      { value: "Faster", label: "Proposals sent the same day, not next week" },
      {
        value: "Visible",
        label: "Budget vs. actuals on every job in real time",
      },
    ],
    ctaText: CTA.PRIMARY,
  },

  faq: {
    title: "Questions landscapers ask",
    items: [
      {
        question: "Does this work for both install and maintenance?",
        answer:
          "Yes. CC handles hardscape installs, planting projects, irrigation work, and recurring maintenance contracts. Install work is tracked as projects. Maintenance work is tracked as services with visit logging. Both share the same cost database and client records.",
      },
      {
        question: "Can I import my nursery pricing?",
        answer:
          "Yes. Import from Excel or CSV. Map your nursery's plant list, sod pricing, mulch costs, and hardscape materials into your cost database. When prices change seasonally, update once — every estimate uses the new numbers.",
      },
      {
        question: "How do work packages work for landscaping?",
        answer:
          "Build reusable bundles — for example, a 'Patio Install' package that includes pavers, base material, polymeric sand, edging, and installation labor. When you estimate a patio job, pull in the package and adjust quantities for the specific property. Production rates and waste factors are built in.",
      },
      {
        question: "Can I track crew hours and costs per job?",
        answer:
          "Yes. Time entries track crew hours with an approval workflow. Actual material and labor costs are logged against the original estimate line items, so you can see budget vs. actuals per job in real time.",
      },
      {
        question: "What about seasonal pricing changes?",
        answer:
          "Update your material costs in the cost database as prices shift. Your estimates automatically use the current pricing. You can also track supplier pricing per vendor to compare between nurseries.",
      },
      {
        question: "Can my team use it?",
        answer:
          "Yes. Add estimators, crew leads, or office staff. Everyone works from the same pricing, the same project data, and the same client records. Role-based access controls who sees what — a crew lead sees their jobs, an office manager sees everything.",
      },
    ],
  },

  cta: {
    title: "Run your landscaping business from one platform.",
    description:
      "Installs, hardscape, maintenance, supplier pricing, crew tracking, invoicing — connected. 7-day free trial.",
    ctaText: CTA.PRIMARY,
    trustIndicators: [
      "7-day free trial",
      "No long-term contracts",
      "Pre-loaded landscaping costs",
      "Cancel anytime",
    ],
  },
};
